import { Modules } from '../enums/modules.enum';
import { PermissionAction, PermissionActionMask, combine } from './permission-actions.enum';

// Single source of truth for every page and tab that can be permissioned. The Roles admin UI
// renders its matrix from this list; route guards look up the surface by `routePath` (or by
// `key` set on `route.data.pageKey`). Adding a new permissionable surface to the app means
// adding an entry here — nowhere else should hardcode page-level permission rules.
//
// Stable-key contract: once a `key` ships to production it is treated as an opaque identifier
// stored in RolePermission rows. Renaming a key is a data migration, not a refactor. Pick keys
// that describe the surface, not the current label (labels change, keys don't).

export type LegacyPermission =
  | 'CorporateAdmin'
  | 'OnboardingAdmin'
  | 'InvoicingAdmin'
  | 'ComplianceAdmin';

export interface PermissionableSurface {
  key: string;
  label: string;
  module: Modules;
  // Set on tab/sub-view entries so the admin matrix can render the hierarchy and the role
  // editor can offer "select all under this parent" shortcuts.
  parentKey?: string;
  // The universe of actions that make sense for this surface — i.e. the columns the matrix
  // will render. A role row stores a subset (AND-bitmask) of these.
  supportedActions: PermissionActionMask;
  // Angular route used by the guard. Omitted for tabs (the parent route is what's guarded;
  // the tab visibility is decided by the host component reading the surface's permission).
  routePath?: string;
  // Records the hardcoded `data.permissions.only` that protected this route before the role
  // system landed. Drives the Phase 7 seed migration that grants equivalent role rows to
  // existing admins so nobody loses access on cutover.
  legacyPermission?: LegacyPermission;
}

// Common action-mask aliases — kept here rather than inlined so the matrix's column set is
// consistent across surfaces of the same "kind" (e.g. every list-of-documents page exposes
// the same Approve/Void/Export options even if today's role bundles don't grant them).
const VIEW         = PermissionAction.View;
const VIEW_EXPORT  = combine(PermissionAction.View, PermissionAction.Export);
const VIEW_EDIT    = combine(PermissionAction.View, PermissionAction.Edit);
const CRUD         = combine(PermissionAction.View, PermissionAction.Create, PermissionAction.Edit, PermissionAction.Delete);
const CRUD_EXPORT  = combine(CRUD, PermissionAction.Export);
const DOCUMENT     = combine(CRUD, PermissionAction.Void, PermissionAction.Approve, PermissionAction.Export);

export const PAGE_REGISTRY: readonly PermissionableSurface[] = [
  // ─── Corporate: home, settings, account ─────────────────────────────────────────────────
  { key: 'corporate.home', label: 'Home',
    module: Modules.Corporate, supportedActions: VIEW, routePath: '' },

  { key: 'corporate.notifications', label: 'Notifications',
    module: Modules.Corporate, supportedActions: VIEW, routePath: 'notifications' },

  { key: 'corporate.user-settings', label: 'User Settings',
    module: Modules.Corporate, supportedActions: VIEW_EDIT, routePath: 'settings/user' },

  { key: 'corporate.onboarding-company', label: 'Onboarding Company',
    module: Modules.Onboarding, supportedActions: VIEW_EDIT, routePath: 'onboarding/company' },

  { key: 'corporate.organization-settings', label: 'Organization Settings',
    module: Modules.Onboarding, supportedActions: VIEW_EDIT,
    routePath: 'settings/organization', legacyPermission: 'OnboardingAdmin' },
  { key: 'corporate.organization-settings.company', label: 'Company',
    module: Modules.Onboarding, parentKey: 'corporate.organization-settings',
    supportedActions: VIEW_EDIT },
  { key: 'corporate.organization-settings.create-company', label: 'Create Company',
    module: Modules.Onboarding, parentKey: 'corporate.organization-settings',
    supportedActions: combine(PermissionAction.View, PermissionAction.Create) },
  { key: 'corporate.organization-settings.organization-services', label: 'Organization Services',
    module: Modules.Onboarding, parentKey: 'corporate.organization-settings',
    supportedActions: CRUD },
  { key: 'corporate.organization-settings.users', label: 'Users',
    module: Modules.Onboarding, parentKey: 'corporate.organization-settings',
    supportedActions: CRUD },

  { key: 'corporate.company-settings', label: 'Company Settings',
    module: Modules.Onboarding, supportedActions: VIEW_EDIT,
    routePath: 'settings/company/:companyId', legacyPermission: 'OnboardingAdmin' },
  { key: 'corporate.company-settings-callback', label: 'Company Settings (Setup)',
    module: Modules.Onboarding, supportedActions: VIEW_EDIT,
    routePath: 'settings/callback/:companyId', legacyPermission: 'OnboardingAdmin' },

  // ─── Corporate: forms / introducers / employees ─────────────────────────────────────────
  { key: 'corporate.introducer.create', label: 'Create Introducer',
    module: Modules.Corporate, supportedActions: combine(PermissionAction.View, PermissionAction.Create),
    routePath: 'create-form/2' },
  { key: 'corporate.introducer.edit', label: 'Edit Introducer',
    module: Modules.Corporate, supportedActions: VIEW_EDIT, routePath: 'edit-introducer/:id' },

  { key: 'corporate.contact-form.create', label: 'Create Contact Form',
    module: Modules.Corporate, supportedActions: combine(PermissionAction.View, PermissionAction.Create),
    routePath: 'create-form/:id' },
  { key: 'corporate.contact-form.edit', label: 'Edit Contact Form',
    module: Modules.Corporate, supportedActions: VIEW_EDIT, routePath: 'edit-form/:id' },

  { key: 'corporate.forms-table', label: 'Forms Table',
    module: Modules.Corporate, supportedActions: VIEW_EXPORT, routePath: 'forms-table' },

  { key: 'corporate.form-constructor', label: 'Form Constructor',
    module: Modules.Onboarding, supportedActions: CRUD,
    routePath: 'form-constructor', legacyPermission: 'OnboardingAdmin' },

  // ─── Corporate: risk / compliance ───────────────────────────────────────────────────────
  { key: 'corporate.risk-settings', label: 'Risk Assessment Settings',
    module: Modules.Risk, supportedActions: VIEW_EDIT,
    routePath: 'corporate/risk-settings', legacyPermission: 'CorporateAdmin' },
  { key: 'corporate.risk-assessment', label: 'Risk Assessment',
    module: Modules.Risk, supportedActions: combine(CRUD, PermissionAction.Approve),
    routePath: 'risk-assessment/answers/:formId/:submittedFormId/:id/:businessId' },
  { key: 'corporate.paf-document', label: 'PAF Document',
    module: Modules.Risk, supportedActions: VIEW_EXPORT, routePath: 'paf-Document' },

  // ─── Corporate: reports / audit / admin tooling ─────────────────────────────────────────
  { key: 'corporate.time-sheet', label: 'Time Sheet',
    module: Modules.Corporate, supportedActions: combine(CRUD, PermissionAction.Approve),
    routePath: 'time-sheet' },

  { key: 'corporate.profitability-report', label: 'Profitability Report',
    module: Modules.Corporate, supportedActions: VIEW_EXPORT,
    routePath: 'profitability-report', legacyPermission: 'CorporateAdmin' },
  { key: 'corporate.profitability-report.dashboard', label: 'Dashboard',
    module: Modules.Corporate, parentKey: 'corporate.profitability-report',
    supportedActions: VIEW_EXPORT },
  { key: 'corporate.profitability-report.detailed', label: 'Detailed Report',
    module: Modules.Corporate, parentKey: 'corporate.profitability-report',
    supportedActions: VIEW_EXPORT },

  { key: 'corporate.log-changes', label: 'Log Changes',
    module: Modules.Corporate, supportedActions: VIEW_EXPORT,
    routePath: 'log-changes', legacyPermission: 'CorporateAdmin' },

  { key: 'corporate.pending-changes', label: 'Pending Changes',
    module: Modules.Corporate, supportedActions: combine(VIEW, PermissionAction.Approve),
    routePath: 'admin/pending-changes', legacyPermission: 'CorporateAdmin' },

  { key: 'corporate.audit-logs', label: 'Audit Logs',
    module: Modules.Corporate, supportedActions: VIEW_EXPORT,
    routePath: 'audit-logs', legacyPermission: 'CorporateAdmin' },

  { key: 'corporate.reports', label: 'Corporate Reports',
    module: Modules.Corporate, supportedActions: VIEW_EXPORT,
    routePath: 'reports', legacyPermission: 'CorporateAdmin' },

  // ─── Corporate: documents (preview / print) ─────────────────────────────────────────────
  { key: 'corporate.documents.client-referral-agreement', label: 'Client Referral Agreement',
    module: Modules.Corporate, supportedActions: VIEW_EXPORT, routePath: 'client-referral-agreement' },
  { key: 'corporate.documents.nda-agreement', label: 'NDA Agreement',
    module: Modules.Corporate, supportedActions: VIEW_EXPORT, routePath: 'nda-agreement' },
  { key: 'corporate.documents.engagement-agreement', label: 'Engagement Agreement',
    module: Modules.Corporate, supportedActions: VIEW_EXPORT, routePath: 'engagement-agreement' },
  { key: 'corporate.documents.ariadni-cyl1', label: 'Ariadni CY-L1',
    module: Modules.Corporate, supportedActions: VIEW_EXPORT, routePath: 'ariadni-cyl1' },
  { key: 'corporate.documents.e-codes-application', label: 'E-Codes Application',
    module: Modules.Corporate, supportedActions: VIEW_EXPORT, routePath: 'e-codes-application' },
  { key: 'corporate.documents.auth-representatives', label: 'Authorized Representatives',
    module: Modules.Corporate, supportedActions: VIEW_EXPORT, routePath: 'auth-representatives' },
  { key: 'corporate.documents.authorization-form', label: 'Authorization Form',
    module: Modules.Corporate, supportedActions: VIEW_EXPORT, routePath: 'authorization-form' },
  { key: 'corporate.documents.m121-english', label: 'M121 (English)',
    module: Modules.Corporate, supportedActions: VIEW_EXPORT, routePath: 'm121-english' },
  { key: 'corporate.documents.m121-greek', label: 'M121 (Greek)',
    module: Modules.Corporate, supportedActions: VIEW_EXPORT, routePath: 'm121-greek' },
  { key: 'corporate.documents.dividends-certificate', label: 'Dividends Certificate',
    module: Modules.Corporate, supportedActions: VIEW_EXPORT, routePath: 'dividends-certificate' },

  // ─── Corporate: file system & sharing ───────────────────────────────────────────────────
  { key: 'corporate.file-system', label: 'File System',
    module: Modules.Corporate, supportedActions: CRUD_EXPORT,
    routePath: 'file-system', legacyPermission: 'CorporateAdmin' },
  { key: 'corporate.share-form', label: 'Share Form',
    module: Modules.Corporate, supportedActions: CRUD,
    routePath: 'share-form', legacyPermission: 'CorporateAdmin' },

  // ─── Corporate: planner / phonebook / notes ─────────────────────────────────────────────
  { key: 'corporate.task-planner', label: 'Task Planner',
    module: Modules.Corporate, supportedActions: CRUD, routePath: 'task-planner' },
  { key: 'corporate.reference-numbers', label: 'Reference Numbers',
    module: Modules.Corporate, supportedActions: CRUD, routePath: 'reference-numbers' },
  { key: 'corporate.company-shares', label: 'Company Shares',
    module: Modules.Corporate, supportedActions: CRUD, routePath: 'company-shares' },
  { key: 'corporate.phonebook', label: 'Phonebook',
    module: Modules.Corporate, supportedActions: CRUD_EXPORT, routePath: 'phonebook' },
  { key: 'corporate.meeting-note', label: 'Meeting Note',
    module: Modules.Corporate, supportedActions: CRUD, routePath: 'meeting-note' },
  { key: 'corporate.tagged-notes', label: 'Tagged Notes',
    module: Modules.Corporate, supportedActions: CRUD, routePath: 'tagged-notes' },
  { key: 'corporate.important-dates', label: 'Important Dates',
    module: Modules.Corporate, supportedActions: CRUD, routePath: 'important-dates' },

  // ─── Corporate: employee dashboard (children of `employee/:id`) ─────────────────────────
  { key: 'corporate.employee', label: 'Employee Dashboard',
    module: Modules.Corporate, supportedActions: VIEW, routePath: 'employee/:id' },
  { key: 'corporate.employee.profile-details',         label: 'Profile Details',
    module: Modules.Corporate, parentKey: 'corporate.employee', supportedActions: VIEW_EDIT },
  { key: 'corporate.employee.leave-allowance',         label: 'Leave Allowance',
    module: Modules.Corporate, parentKey: 'corporate.employee', supportedActions: VIEW_EDIT },
  { key: 'corporate.employee.salary-details',          label: 'Salary Details',
    module: Modules.Corporate, parentKey: 'corporate.employee', supportedActions: VIEW_EDIT },
  { key: 'corporate.employee.leave-overview',          label: 'Leave Overview',
    module: Modules.Corporate, parentKey: 'corporate.employee', supportedActions: VIEW },
  { key: 'corporate.employee.leave-requests',          label: 'Leave Requests',
    module: Modules.Corporate, parentKey: 'corporate.employee', supportedActions: CRUD },
  { key: 'corporate.employee.upcoming-leaves',         label: 'Upcoming Leaves',
    module: Modules.Corporate, parentKey: 'corporate.employee', supportedActions: VIEW },
  { key: 'corporate.employee.new-leave',               label: 'New Leave',
    module: Modules.Corporate, parentKey: 'corporate.employee',
    supportedActions: combine(PermissionAction.View, PermissionAction.Create) },
  { key: 'corporate.employee.pending-leaves',          label: 'Pending Leaves',
    module: Modules.Corporate, parentKey: 'corporate.employee',
    supportedActions: combine(VIEW, PermissionAction.Approve) },
  { key: 'corporate.employee.all-employee-leaves',     label: 'All Employee Leaves',
    module: Modules.Corporate, parentKey: 'corporate.employee', supportedActions: VIEW_EXPORT },
  { key: 'corporate.employee.all-employees-overview',  label: 'All Employees Leave Overview',
    module: Modules.Corporate, parentKey: 'corporate.employee', supportedActions: VIEW_EXPORT },
  { key: 'corporate.employee.leave-calendar',          label: 'Leave Calendar',
    module: Modules.Corporate, parentKey: 'corporate.employee', supportedActions: VIEW },
  { key: 'corporate.employee.holiday-management',      label: 'Holiday Management',
    module: Modules.Corporate, parentKey: 'corporate.employee', supportedActions: CRUD },
  { key: 'corporate.employee.positions',               label: 'Positions',
    module: Modules.Corporate, parentKey: 'corporate.employee', supportedActions: CRUD },
  { key: 'corporate.employee.assignments',             label: 'Assignments',
    module: Modules.Corporate, parentKey: 'corporate.employee', supportedActions: CRUD },
  { key: 'corporate.employee.salaries',                label: 'Salaries',
    module: Modules.Corporate, parentKey: 'corporate.employee', supportedActions: CRUD_EXPORT },
  { key: 'corporate.employee.departments',             label: 'Departments',
    module: Modules.Corporate, parentKey: 'corporate.employee', supportedActions: CRUD },
  { key: 'corporate.employee.org-chart',               label: 'Org Chart',
    module: Modules.Corporate, parentKey: 'corporate.employee', supportedActions: VIEW },

  // ─── Global Requests (children of `global-requests`) ────────────────────────────────────
  { key: 'corporate.global-requests', label: 'Global Requests',
    module: Modules.Corporate, supportedActions: VIEW, routePath: 'global-requests' },
  { key: 'corporate.global-requests.submit', label: 'Submit Request',
    module: Modules.Corporate, parentKey: 'corporate.global-requests',
    supportedActions: combine(PermissionAction.View, PermissionAction.Create) },
  { key: 'corporate.global-requests.my-requests', label: 'My Requests',
    module: Modules.Corporate, parentKey: 'corporate.global-requests', supportedActions: VIEW },
  { key: 'corporate.global-requests.inbox', label: 'Approvals Inbox',
    module: Modules.Corporate, parentKey: 'corporate.global-requests',
    supportedActions: combine(VIEW, PermissionAction.Approve) },
  { key: 'corporate.global-requests.access-management', label: 'Access Management',
    module: Modules.Corporate, parentKey: 'corporate.global-requests', supportedActions: CRUD },

  // ─── Invoicing: dashboards ──────────────────────────────────────────────────────────────
  { key: 'invoicing.dashboard', label: 'Invoicing Dashboard',
    module: Modules.Invoicing, supportedActions: VIEW_EXPORT, routePath: 'invoicing-dashboard' },
  // Tabs rendered by invoicing-dashboard.component.html (Vendors / Expenses / Payments /
  // Payment Methods / Bank Accounts / VAT Rates / Number Sequences / Chart Of Accounts /
  // BC Backfill / Sync Logs / Open Banking). Each is also a routable surface below — the tab
  // entries exist so the matrix can grant "show this tab inside the dashboard" independently
  // of whether the user can reach the standalone route. Tabs share parentKey for grouping.
  { key: 'invoicing.dashboard.vendors',            label: 'Vendors (tab)',
    module: Modules.Invoicing, parentKey: 'invoicing.dashboard', supportedActions: VIEW },
  { key: 'invoicing.dashboard.expenses',           label: 'Expenses (tab)',
    module: Modules.Invoicing, parentKey: 'invoicing.dashboard', supportedActions: VIEW },
  { key: 'invoicing.dashboard.payments',           label: 'Payments (tab)',
    module: Modules.Invoicing, parentKey: 'invoicing.dashboard', supportedActions: VIEW },
  { key: 'invoicing.dashboard.payment-methods',    label: 'Payment Methods (tab)',
    module: Modules.Invoicing, parentKey: 'invoicing.dashboard', supportedActions: VIEW },
  { key: 'invoicing.dashboard.bank-accounts',      label: 'Bank Accounts (tab)',
    module: Modules.Invoicing, parentKey: 'invoicing.dashboard', supportedActions: VIEW },
  { key: 'invoicing.dashboard.vat-rates',          label: 'VAT Rates (tab)',
    module: Modules.Invoicing, parentKey: 'invoicing.dashboard', supportedActions: VIEW },
  { key: 'invoicing.dashboard.number-sequences',   label: 'Number Sequences (tab)',
    module: Modules.Invoicing, parentKey: 'invoicing.dashboard', supportedActions: VIEW },
  { key: 'invoicing.dashboard.chart-of-accounts',  label: 'Chart of Accounts (tab)',
    module: Modules.Invoicing, parentKey: 'invoicing.dashboard', supportedActions: VIEW },
  { key: 'invoicing.dashboard.bc-backfill',        label: 'BC Backfill (tab)',
    module: Modules.Invoicing, parentKey: 'invoicing.dashboard', supportedActions: combine(VIEW, PermissionAction.Edit) },
  { key: 'invoicing.dashboard.sync-logs',          label: 'Sync Logs (tab)',
    module: Modules.Invoicing, parentKey: 'invoicing.dashboard', supportedActions: VIEW_EXPORT },
  { key: 'invoicing.dashboard.open-banking',       label: 'Open Banking (tab)',
    module: Modules.Invoicing, parentKey: 'invoicing.dashboard', supportedActions: VIEW },

  { key: 'invoicing.finance-dashboard', label: 'Finance Dashboard',
    module: Modules.Invoicing, supportedActions: VIEW_EXPORT },
  { key: 'invoicing.finance-dashboard.overview', label: 'Overview',
    module: Modules.Invoicing, parentKey: 'invoicing.finance-dashboard', supportedActions: VIEW },
  { key: 'invoicing.finance-dashboard.recurring-invoices', label: 'Recurring Invoices',
    module: Modules.Invoicing, parentKey: 'invoicing.finance-dashboard', supportedActions: CRUD },

  // ─── Invoicing: documents (invoices / quotations / credit notes / receipts) ─────────────
  { key: 'invoicing.invoices', label: 'Invoices',
    module: Modules.Invoicing, supportedActions: DOCUMENT, routePath: 'invoicing/invoices-new' },
  { key: 'invoicing.invoices.create', label: 'Create Invoice',
    module: Modules.Invoicing, parentKey: 'invoicing.invoices',
    supportedActions: combine(PermissionAction.View, PermissionAction.Create),
    routePath: 'invoicing/invoices-new/create' },

  { key: 'invoicing.quotations', label: 'Quotations',
    module: Modules.Invoicing, supportedActions: DOCUMENT, routePath: 'invoicing/quotations' },
  { key: 'invoicing.quotations.create', label: 'Create Quotation',
    module: Modules.Invoicing, parentKey: 'invoicing.quotations',
    supportedActions: combine(PermissionAction.View, PermissionAction.Create),
    routePath: 'invoicing/quotations/create' },

  { key: 'invoicing.credit-notes', label: 'Credit Notes',
    module: Modules.Invoicing, supportedActions: DOCUMENT, routePath: 'invoicing/credit-notes' },
  { key: 'invoicing.credit-notes.create', label: 'Create Credit Note',
    module: Modules.Invoicing, parentKey: 'invoicing.credit-notes',
    supportedActions: combine(PermissionAction.View, PermissionAction.Create),
    routePath: 'invoicing/credit-notes/create' },

  { key: 'invoicing.receipts', label: 'Receipts',
    module: Modules.Invoicing, supportedActions: DOCUMENT, routePath: 'invoicing/receipts' },
  { key: 'invoicing.receipts.create', label: 'Create Receipt',
    module: Modules.Invoicing, parentKey: 'invoicing.receipts',
    supportedActions: combine(PermissionAction.View, PermissionAction.Create),
    routePath: 'invoicing/receipts/create' },

  { key: 'invoicing.all-documents', label: 'All Documents',
    module: Modules.Invoicing, supportedActions: VIEW_EXPORT, routePath: 'invoicing/all-documents' },

  { key: 'invoicing.customer-balance', label: 'Customer Balances',
    module: Modules.Invoicing, supportedActions: VIEW_EXPORT, routePath: 'invoicing/customer-balance' },

  // ─── Invoicing: sales / expenses / vendors ──────────────────────────────────────────────
  { key: 'invoicing.sales', label: 'Sales',
    module: Modules.Invoicing, supportedActions: CRUD_EXPORT, routePath: 'invoicing/sales' },
  { key: 'invoicing.sales.transaction', label: 'Sales Transaction',
    module: Modules.Invoicing, parentKey: 'invoicing.sales', supportedActions: CRUD,
    routePath: 'invoicing/sales-transaction' },
  { key: 'invoicing.sales.transaction-sequences', label: 'Transaction Sequences',
    module: Modules.Invoicing, parentKey: 'invoicing.sales', supportedActions: VIEW_EDIT,
    routePath: 'invoicing/transaction-sequences' },

  { key: 'invoicing.expenses', label: 'Expenses',
    module: Modules.Invoicing, supportedActions: combine(CRUD, PermissionAction.Approve, PermissionAction.Export),
    routePath: 'invoicing/expenses' },
  { key: 'invoicing.expenses.create', label: 'Create Expense',
    module: Modules.Invoicing, parentKey: 'invoicing.expenses',
    supportedActions: combine(PermissionAction.View, PermissionAction.Create),
    routePath: 'invoicing/expenses/create' },
  { key: 'invoicing.disbursements-management', label: 'Disbursements Management',
    module: Modules.Invoicing, supportedActions: CRUD_EXPORT, routePath: 'invoicing/disbursements-management' },
  { key: 'invoicing.actual-expenses-management', label: 'Actual Expenses Management',
    module: Modules.Invoicing, supportedActions: CRUD_EXPORT, routePath: 'invoicing/actual-expenses-management' },

  { key: 'invoicing.vendors', label: 'Vendors',
    module: Modules.Invoicing, supportedActions: CRUD_EXPORT, routePath: 'invoicing/vendors' },
  { key: 'invoicing.vendors.create', label: 'Create Vendor',
    module: Modules.Invoicing, parentKey: 'invoicing.vendors',
    supportedActions: combine(PermissionAction.View, PermissionAction.Create),
    routePath: 'invoicing/vendors/create' },
  { key: 'invoicing.vendors.detail', label: 'Vendor Detail',
    module: Modules.Invoicing, parentKey: 'invoicing.vendors', supportedActions: VIEW_EDIT,
    routePath: 'invoicing/vendors/:vendorId' },
  { key: 'invoicing.vendors.detail.expenses', label: 'Vendor Expenses (tab)',
    module: Modules.Invoicing, parentKey: 'invoicing.vendors.detail', supportedActions: VIEW },
  { key: 'invoicing.vendors.detail.payments', label: 'Vendor Payments (tab)',
    module: Modules.Invoicing, parentKey: 'invoicing.vendors.detail', supportedActions: VIEW },

  // ─── Invoicing: settings / lookups ──────────────────────────────────────────────────────
  { key: 'invoicing.vat-rates', label: 'VAT Rates',
    module: Modules.Invoicing, supportedActions: CRUD, routePath: 'invoicing/vat-rates' },
  { key: 'invoicing.bank-accounts', label: 'Bank Accounts',
    module: Modules.Invoicing, supportedActions: CRUD, routePath: 'invoicing/bank-accounts' },
  { key: 'invoicing.bank-settings', label: 'Bank Settings',
    module: Modules.Invoicing, supportedActions: VIEW_EDIT, routePath: 'invoicing/bank-settings' },
  { key: 'invoicing.number-sequences', label: 'Number Sequences',
    module: Modules.Invoicing, supportedActions: CRUD, routePath: 'invoicing/number-sequences' },
  { key: 'invoicing.payment-methods', label: 'Payment Methods',
    module: Modules.Invoicing, supportedActions: CRUD, routePath: 'invoicing/payment-methods' },
  { key: 'invoicing.payment-methods-new', label: 'Payment Methods (new)',
    module: Modules.Invoicing, supportedActions: CRUD, routePath: 'invoicing/new-payments' },
  { key: 'invoicing.chart-of-accounts', label: 'Chart of Accounts',
    module: Modules.Invoicing, supportedActions: CRUD_EXPORT, routePath: 'invoicing/chart-of-accounts' },
  { key: 'invoicing.reports', label: 'Invoicing Reports',
    module: Modules.Invoicing, supportedActions: VIEW_EXPORT, routePath: 'invoicing/reports' },

  // ─── Invoicing: open banking ────────────────────────────────────────────────────────────
  { key: 'invoicing.open-banking', label: 'Open Banking',
    module: Modules.Invoicing, supportedActions: VIEW_EDIT },
  { key: 'invoicing.open-banking.accounts',      label: 'Accounts',
    module: Modules.Invoicing, parentKey: 'invoicing.open-banking', supportedActions: CRUD },
  { key: 'invoicing.open-banking.transactions',  label: 'Transactions',
    module: Modules.Invoicing, parentKey: 'invoicing.open-banking', supportedActions: VIEW_EXPORT },
  { key: 'invoicing.open-banking.subscriptions', label: 'Subscriptions',
    module: Modules.Invoicing, parentKey: 'invoicing.open-banking', supportedActions: CRUD },
  { key: 'invoicing.open-banking.payments',      label: 'Payments',
    module: Modules.Invoicing, parentKey: 'invoicing.open-banking', supportedActions: VIEW },

  // ─── Invoicing: legacy (pluton) ─────────────────────────────────────────────────────────
  { key: 'invoicing.legacy.customers', label: 'Legacy Customers',
    module: Modules.Invoicing, supportedActions: VIEW_EXPORT, routePath: 'invoicing/legacy-customers' },
  { key: 'invoicing.legacy.items', label: 'Legacy Items',
    module: Modules.Invoicing, supportedActions: VIEW_EXPORT, routePath: 'invoicing/legacy-items' },
  { key: 'invoicing.legacy.receipts', label: 'Legacy Receipts',
    module: Modules.Invoicing, supportedActions: VIEW_EXPORT, routePath: 'invoicing/legacy-receipts' },
  { key: 'invoicing.legacy.sales', label: 'Legacy Sales',
    module: Modules.Invoicing, supportedActions: VIEW_EXPORT, routePath: 'invoicing/legacy-sales' },
  { key: 'invoicing.legacy.statement', label: 'Legacy Statement',
    module: Modules.Invoicing, supportedActions: VIEW_EXPORT, routePath: 'invoicing/legacy-statement' },
  { key: 'invoicing.legacy.quotations', label: 'Legacy Quotations',
    module: Modules.Invoicing, supportedActions: VIEW_EXPORT, routePath: 'invoicing/legacy-quotations' },
  { key: 'invoicing.legacy.credit-notes', label: 'Legacy Credit Notes',
    module: Modules.Invoicing, supportedActions: VIEW_EXPORT, routePath: 'invoicing/legacy-credit-notes' },
  { key: 'invoicing.legacy.receivables', label: 'Legacy Receivables',
    module: Modules.Invoicing, supportedActions: VIEW_EXPORT, routePath: 'invoicing/legacy-receivables' },

  // Pluton-module mirrors (mounted under /pluton-invoicing/* — same components, different
  // path prefix during the cutover from the old Pluton API to the new invoicing module).
  { key: 'invoicing.pluton.dashboard', label: 'Pluton Invoicing Dashboard',
    module: Modules.Invoicing, supportedActions: VIEW, routePath: 'pluton-invoicing-dashboard' },
  { key: 'invoicing.pluton.legacy.customers', label: 'Pluton Customers',
    module: Modules.Invoicing, supportedActions: VIEW_EXPORT, routePath: 'pluton-invoicing/legacy-customers' },
  { key: 'invoicing.pluton.legacy.items', label: 'Pluton Items',
    module: Modules.Invoicing, supportedActions: VIEW_EXPORT, routePath: 'pluton-invoicing/legacy-items' },
  { key: 'invoicing.pluton.legacy.receipts', label: 'Pluton Receipts',
    module: Modules.Invoicing, supportedActions: VIEW_EXPORT, routePath: 'pluton-invoicing/legacy-receipts' },
  { key: 'invoicing.pluton.legacy.sales', label: 'Pluton Sales',
    module: Modules.Invoicing, supportedActions: VIEW_EXPORT, routePath: 'pluton-invoicing/legacy-sales' },
  { key: 'invoicing.pluton.legacy.statement', label: 'Pluton Statement',
    module: Modules.Invoicing, supportedActions: VIEW_EXPORT, routePath: 'pluton-invoicing/legacy-statement' },
  { key: 'invoicing.pluton.legacy.receivables', label: 'Pluton Receivables',
    module: Modules.Invoicing, supportedActions: VIEW_EXPORT, routePath: 'pluton-invoicing/legacy-receivables' },
];

// Lookups built once, used everywhere — keep these alongside the registry so callers don't
// have to recompute the indexes per render.
export const PAGE_REGISTRY_BY_KEY: ReadonlyMap<string, PermissionableSurface> =
  new Map(PAGE_REGISTRY.map(s => [s.key, s]));

export const PAGE_REGISTRY_BY_ROUTE: ReadonlyMap<string, PermissionableSurface> =
  new Map(
    PAGE_REGISTRY
      .filter((s): s is PermissionableSurface & { routePath: string } => !!s.routePath)
      .map(s => [s.routePath, s])
  );

export function getChildren(parentKey: string): PermissionableSurface[] {
  return PAGE_REGISTRY.filter(s => s.parentKey === parentKey);
}

export function getTopLevelByModule(module: Modules): PermissionableSurface[] {
  return PAGE_REGISTRY.filter(s => s.module === module && !s.parentKey);
}
